import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cx } from '@/lib/cx'

type Variant = 'primary' | 'secondary' | 'ghost' | 'onDark'
type Size = 'default' | 'large' | 'small'

const BASE =
  'inline-flex items-center justify-center gap-2.5 rounded-[0.5rem] font-display font-semibold ' +
  'tracking-[0.01em] whitespace-nowrap transition-[background-color,color,box-shadow,transform] ' +
  'duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:translate-y-px disabled:pointer-events-none ' +
  'disabled:opacity-55'

const VARIANTS: Record<Variant, string> = {
  // #4E7522 carries white text at 5.40:1. The logo green (#689030) does not, which is
  // why it is never the button colour.
  primary:
    'bg-brand-press text-white shadow-lift hover:bg-[#446620] hover:shadow-[0_6px_20px_-6px_rgb(78_117_34/0.5)]',
  secondary: 'border border-line-2 bg-surface text-ink hover:border-ink hover:bg-raise',
  ghost: 'text-ink hover:bg-raise',
  onDark:
    'border border-white/25 bg-white/5 text-white hover:border-brand-lit hover:bg-white/10',
}

const SIZES: Record<Size, string> = {
  // 48px tall: a comfortable touch target at every breakpoint, not just the phone one.
  default: 'min-h-12 px-5 text-[0.9375rem]',
  large: 'min-h-[3.375rem] px-7 text-base',
  small: 'min-h-10 px-4 text-sm',
}

interface Common {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
}

/** An internal link that looks like a button. */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'default',
  className,
  children,
  ...rest
}: Common & { to: string } & Omit<
    ComponentPropsWithoutRef<typeof Link>,
    'to' | 'className' | 'children'
  >) {
  return (
    <Link to={to} className={cx(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {children}
    </Link>
  )
}

/** An external or protocol link (tel:, mailto:) that looks like a button. */
export function ButtonAnchor({
  href,
  variant = 'secondary',
  size = 'default',
  className,
  children,
  ...rest
}: Common & ComponentPropsWithoutRef<'a'>) {
  return (
    <a href={href} className={cx(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {children}
    </a>
  )
}

export function Button({
  variant = 'primary',
  size = 'default',
  className,
  children,
  type = 'button',
  ...rest
}: Common & ComponentPropsWithoutRef<'button'>) {
  return (
    <button
      type={type}
      className={cx(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {children}
    </button>
  )
}

/** The quiet inline link used under a CTA pair or at the end of a section. */
export function TextLink({
  to,
  className,
  children,
}: {
  to: string
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      className={cx(
        'group font-display inline-flex min-h-11 items-center gap-2 text-[0.9375rem] font-semibold',
        'decoration-line-2 underline underline-offset-[0.4rem] transition-colors',
        'hover:text-brand-press hover:decoration-brand-press',
        className,
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className="transition-transform duration-200 group-hover:translate-x-0.5"
      >
        →
      </span>
    </Link>
  )
}
